// =============================================================================
// KATALOG WYDAŃ — co da się pobrać w dziale „Aplikacje i urządzenia".
//
// DLACZEGO KATALOG W KODZIE, A NIE ZAPYTANIE O LISTĘ. `app_versions` ma
// włączony RLS i ZERO polityk (migracja 183), więc z przeglądarki nie ma do
// niej `SELECT`-a — jedyną drogą jest RPC `get_app_version`, a ono odpowiada
// na JEDNĄ trójkę (aplikacja, platforma, kanał) naraz. Nie da się więc
// zapytać „co jest wydane"; trzeba wiedzieć, o co pytać. Ta tablica jest tą
// wiedzą i to ona, a nie baza, decyduje, jakie pozycje w ogóle widać.
//
// KAŻDA POZYCJA MUSI ZGADZAĆ SIĘ Z WORKFLOWEM WYDAWNICZYM. `app` i `platform`
// to dosłownie wartości, które wpisuje do rejestru odpowiedni
// `.github/workflows/*-release.yml`. Rozjazd niczego nie wywala — po prostu
// cicho chowa pozycję, bo RPC odpowie `{update_available:false}`.
// =============================================================================

/**
 * Rodzina urządzeń = jedna zakładka działu.
 *
 * `integracje` NIE jest podrodzajem `serwery`, choć jedno i drugie stoi na
 * maszynie w sieci klienta. Zakładka serwerów wydruku jest PODGLĄDEM kartoteki
 * organu druku (`print_agents`) i linkuje zarządzanie do `/admin/printing`;
 * agent integracyjny ma własną kartotekę (`receptor_agents`, migracja 729)
 * i własne zarządzanie. Wspólna zakładka byłaby jedną listą złożoną z dwóch
 * niepowiązanych kartotek, z dwoma różnymi miejscami zarządzania.
 */
export type RodzinaUrzadzen = 'telewizory' | 'mobilne' | 'stanowiska' | 'serwery' | 'integracje';

export interface WydanieZRejestru {
  update_available: boolean;
  version_code?: number;
  version_name?: string | null;
  storage_path?: string;
  sha256?: string;
  changelog?: string | null;
}

export interface PozycjaWydania {
  /** Klucz pozycji w katalogu — używany jako klucz Reacta i w mapie wydań. */
  klucz: string;
  rodzina: RodzinaUrzadzen;
  /** `app_versions.app` — dokładnie ta wartość, którą pisze workflow. */
  app: string;
  /** `app_versions.platform` — surowe `process.platform` tam, gdzie tak wydaje workflow. */
  platform: string;
  etykieta: string;
  opis: string;
  /** Platforma wiodąca rodziny: akcentowany przycisk i to z niej bierzemy numer wersji. */
  glowna?: boolean;
  /** Czy z tego pliku robimy kod QR (ma sens tylko tam, gdzie plik pobiera się telefonem). */
  qr?: boolean;
  /**
   * Ścieżka do pliku PIERWSZEJ INSTALACJI, gdy różni się od artefaktu OTA.
   *
   * Serwer wydruku jest jedyną rodziną, w której te dwie rzeczy to dwa różne
   * pliki: `storage_path` z rejestru wskazuje GOŁE BINARIUM, które hak
   * pre-startowy usługi podmienia w miejscu przy aktualizacji. Podanie go
   * administratorowi jako „instalatora" dałoby mu plik, który niczego nie
   * instaluje i nie rejestruje usługi. Instalator i pakiet `.deb` leżą w tym
   * samym katalogu kubełka i wysyła je ten sam krok workflow, co wiersz
   * w rejestrze — a wiersz idzie PO wysyłce plików, więc jego obecność
   * dowodzi, że plik obok już tam jest.
   */
  instalator?: (wersja: string) => string;
}

/**
 * Panel produkcyjny wychodzi na cztery platformy (`S1 §2`) i każda ma własny
 * wiersz w rejestrze, bo każda to inny plik z własną sumą kontrolną.
 * `linux-x64` i `linux-arm64` są OSOBNE, choć oba to „Linux": pomylenie ich
 * kończy się komunikatem „Exec format error" przy stole.
 */
export const KATALOG_WYDAN: readonly PozycjaWydania[] = [
  {
    klucz: 'universe-android',
    rodzina: 'mobilne',
    app: 'universe',
    platform: 'android',
    etykieta: 'Pobierz instalator APK',
    opis: 'Android — tablet lub telefon pracownika',
    glowna: true,
    qr: true,
  },
  {
    klucz: 'universe-tv-android',
    rodzina: 'telewizory',
    app: 'universe-tv',
    platform: 'android',
    etykieta: 'Pobierz APK na TV',
    opis: 'Android TV — żywa tablica na ścianie',
    glowna: true,
    qr: true,
  },
  {
    klucz: 'panel-android',
    rodzina: 'stanowiska',
    app: 'fabrica-panel',
    platform: 'android',
    etykieta: 'Tablet (APK)',
    opis: 'Android — tablet stanowiskowy',
    glowna: true,
    qr: true,
  },
  {
    klucz: 'panel-windows',
    rodzina: 'stanowiska',
    app: 'fabrica-panel',
    platform: 'windows',
    etykieta: 'Windows (instalator .exe)',
    opis: 'Pecet przy stole, Windows 10/11 x64 — uruchom i gotowe',
  },
  {
    klucz: 'panel-linux-x64',
    rodzina: 'stanowiska',
    app: 'fabrica-panel',
    platform: 'linux-x64',
    etykieta: 'Linux x64 (instalator .run)',
    opis: 'Pecet przy stole, Linux x64 — sudo ./plik.run',
  },
  {
    klucz: 'panel-linux-arm64',
    rodzina: 'stanowiska',
    app: 'fabrica-panel',
    platform: 'linux-arm64',
    etykieta: 'Raspberry Pi (instalator .run)',
    opis: 'Raspberry Pi 4/5 (64-bit) — sudo ./plik.run',
  },
  // ── Serwer wydruku: Insolitum Cloud Print ──────────────────────────────
  //
  // DLACZEGO TU NIE MA JUŻ `print-agent`. Do 0.3.x rodzina „serwery" oferowała
  // instalator BEZGŁOWEJ usługi WinSW. Cloud Print 0.4.0 zastępuje ją
  // aplikacją i jego instalator sam tę usługę zatrzymuje i wyrejestrowuje.
  // Zostawienie obu pozycji obok siebie byłoby pułapką: dwa agenty na jednej
  // maszynie pobierają zadania z TEJ SAMEJ kolejki, więc część wydruków
  // wychodziłaby z tego, który akurat pierwszy sięgnął po zadanie. Usunięcie
  // pozycji nie odcina nikogo od aktualizacji — OTA istniejących instalacji
  // 0.3.x idzie przez `update-check` print-core'a, a nie przez ten panel.
  //
  // DLACZEGO BEZ `instalator:`. To jest jedyna różnica wobec print-agenta
  // i akurat ona upraszcza: tam `storage_path` w rejestrze wskazywał gołe
  // binarium OTA, więc administrator musiał dostać INNY plik. Tu każdy
  // z trzech wierszy rejestru wskazuje wprost artefakt pierwszej instalacji
  // (`…-Setup-<wersja>.exe`, `.AppImage`, `.deb`), bo `electron-updater`
  // aktualizuje się tym samym plikiem, którym się instaluje.
  //
  // NAZWY PLATFORM SĄ Z KROKU ZAPISU DO REJESTRU w `cloud-print-release.yml`
  // (`zapisz 'windows'`, `zapisz 'linux-appimage'`, `zapisz 'linux-deb'`), a nie
  // z `process.platform` — inaczej niż w print-agencie, i pomylenie tych dwóch
  // konwencji daje ciszę: RPC odpowie `{update_available:false}` i pozycja po
  // prostu zniknie z listy.
  {
    klucz: 'cloud-print-windows',
    rodzina: 'serwery',
    app: 'cloud-print',
    platform: 'windows',
    etykieta: 'Windows (instalator .exe)',
    opis: 'Windows 10/11 lub Server x64 — aplikacja z ikoną w zasobniku i sterownik druku PDF',
    glowna: true,
  },
  {
    klucz: 'cloud-print-linux-deb',
    rodzina: 'serwery',
    app: 'cloud-print',
    platform: 'linux-deb',
    etykieta: 'Debian/Ubuntu (pakiet .deb)',
    opis: 'Linux x64 z CUPS — sudo apt install ./plik.deb',
  },
  {
    klucz: 'cloud-print-linux-appimage',
    rodzina: 'serwery',
    app: 'cloud-print',
    platform: 'linux-appimage',
    etykieta: 'Linux (AppImage)',
    opis: 'Dowolna dystrybucja x64 — chmod +x i uruchom, bez instalacji',
  },
  // ── Integracje: agent Receptor 3.0 Ready ──────────────────────────────
  //
  // Agent stoi NA TYM SAMYM SERWERZE co Receptor i jego baza SQL Server.
  // Dobija z sieci klienta na zewnątrz — nic nie wchodzi do środka — więc
  // zapora zakładowa nie musi przepuszczać niczego do wewnątrz.
  //
  // BEZ `instalator:`, tak samo jak Cloud Print i z tego samego powodu:
  // `storage_path` w rejestrze wskazuje wprost artefakt pierwszej instalacji,
  // bo `electron-updater` aktualizuje się tym samym plikiem, którym się
  // instaluje. Pole ma sens wyłącznie tam, gdzie OTA podmienia gołe binarium.
  //
  // BEZ `qr:`, bo tego pliku nie pobiera się telefonem — pobiera go
  // administrator na serwerze.
  //
  // NAZWY PLATFORM MUSZĄ ZGADZAĆ SIĘ Z KROKIEM ZAPISU DO REJESTRU
  // w `receptor-agent-release.yml`. Rozjazd nie wywala niczego — RPC odpowie
  // `{update_available:false}` i pozycja po prostu zniknie z listy.
  {
    klucz: 'receptor-agent-windows',
    rodzina: 'integracje',
    app: 'receptor-agent',
    platform: 'windows',
    etykieta: 'Windows (instalator .exe)',
    opis: 'Serwer z Receptorem, Windows x64 — zainstaluj i sparuj kodem',
    glowna: true,
  },
  {
    klucz: 'receptor-agent-linux-deb',
    rodzina: 'integracje',
    app: 'receptor-agent',
    platform: 'linux-deb',
    etykieta: 'Debian/Ubuntu (pakiet .deb)',
    opis: 'Serwer z Receptorem, Linux x64 — sudo apt install ./plik.deb',
  },
  {
    klucz: 'receptor-agent-linux-appimage',
    rodzina: 'integracje',
    app: 'receptor-agent',
    platform: 'linux-appimage',
    etykieta: 'Linux (AppImage)',
    opis: 'Dowolna dystrybucja x64 — chmod +x i uruchom, bez instalacji',
  },
];

export function pozycjeRodziny(rodzina: RodzinaUrzadzen): PozycjaWydania[] {
  return KATALOG_WYDAN.filter((p) => p.rodzina === rodzina);
}

/**
 * Publiczny adres pliku w kubełku `app-releases` (odczyt publiczny — migracja
 * 183). Zwraca `null`, gdy nie znamy adresu Supabase albo ścieżki: przycisk do
 * pliku, którego nie ma, jest gorszy niż jego brak.
 */
export function urlPliku(
  supabaseUrl: string | undefined,
  sciezka: string | undefined
): string | null {
  if (!supabaseUrl || !sciezka) return null;
  return `${supabaseUrl}/storage/v1/object/public/app-releases/${sciezka}`;
}

/**
 * Adres pliku DO POBRANIA dla danej pozycji katalogu.
 *
 * Kolejność jest istotna: gdy pozycja ma własny plik instalacyjny, bierzemy
 * jego ścieżkę (złożoną z `version_name`), a `storage_path` z rejestru zostaje
 * tam, gdzie jego miejsce — w aktualizacji OTA.
 */
export function urlDoPobrania(
  supabaseUrl: string | undefined,
  pozycja: PozycjaWydania,
  wydanie: WydanieZRejestru | undefined
): string | null {
  if (!wydanie?.storage_path) return null;
  if (pozycja.instalator && wydanie.version_name) {
    return urlPliku(supabaseUrl, pozycja.instalator(wydanie.version_name));
  }
  return urlPliku(supabaseUrl, wydanie.storage_path);
}
